import { t } from '@lingui/macro';

import { AdminPages } from '../AdminLinkUtils';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { standardizeTags } from '../../../utilities/FormattingUtilities';
import { NAME_FIELD } from '../service-tests/ServiceTestAdminConstants';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { MaterialUiIcon } from '../../icon';

export const NOTIFICATIONS_FUNCTION_NAME = AdminPages.Notifications;
export const NOTIFICATIONS_CONFIG_API_URL = '/ipm/v1/admin/notificationpolicy';
export const NOTIFICATIONS_CONFIG_QUERY_URL = `${NOTIFICATIONS_CONFIG_API_URL}/listByType`;

export const NOTIFICATIONS_ENABLED_FIELD = 'enabled';
export const NOTIFICATIONS_ALERTLEVEL_FIELD = 'alertLevel';
export const NOTIFICATIONS_NOTIFY_FIELD = 'notify';

export const NOTIFICATIONS_SNMPTRAPSENDTO_FIELD = 'snmpTrapSendTo';

export const NOTIFY_EMAIL_FIELD = 'email';
export const NOTIFY_ALL_FIELD = 'all';
export const NOTIFY_NOTIFY_IDS_FIELD = 'ids';

export const SNMP_SERVER_FIELD = 'server';
export const SNMP_VERSION_FIELD = 'version';
export const SNMP_COMMUNITY_FIELD = 'community';
export const SNMP_COMMUNITY_VALUE_FIELD = 'community.value';
export const SNMP_USERNAME_FIELD = 'userName';
export const SNMP_CONTEXT_FIELD = 'context';
export const SNMP_PROTOCOL_FIELD = 'protocol';
export const SNMP_AUTHPASSWORD_FIELD = 'authPassword';
export const SNMP_AUTHPASSWORD_VALUE_FIELD = 'authPassword.value';
export const SNMP_PRIVACYPROTOCOL_FIELD = 'privacyProtocol';
export const SNMP_PRIVACYPASSWORD_FIELD = 'privacyPassword';
export const SNMP_PRIVACYPASSWORD_VALUE_FIELD = 'privacyPassword.value';

export const NOTIFY_LIST_TABLE_NAME_FIELD = 'name';
export const NOTIFY_LIST_TABLE_DESCRIPTION_FIELD = 'description';
export const NOTIFY_LIST_TABLE_NOTES_FIELD = 'notes';
export const NOTIFY_LIST_TABLE_ICON_FIELD = 'testTypeObject.details.icon';
export const NOTIFY_LIST_TABLE_TESTTYPEOBJECT_FIELD = 'testTypeObject';
export const NOTIFY_LIST_TABLE_TAGS_FIELD = 'tags';
export const NOTIFY_LIST_TABLE_DEVICE_NAME_FIELD = 'deviceName';
export const NOTIFY_LIST_TABLE_DEVICE_IPADDRESS_FIELD = 'deviceIpAddress';
export const NOTIFY_LIST_TABLE_IPADDRESS_FIELD = 'ipAddress';

export const SELECTION_MODE = {
    all: 'all',
    selection: 'selection',
};

export const ALERT_DEGRADED = 2;
export const ALERT_EXCESSIVE = 3;
export const ALERT_CRITICAL = 4;

export const ALERT_CRITICAL_OPTION = 'critical';
export const ALERT_EXCESSIVE_OPTION = 'excessive';
export const ALERT_DEGRADED_OPTION = 'degraded';

export const ALERT_LEVEL_OPTIONS = {
    [ALERT_CRITICAL_OPTION]: {
        display: t`Critical`,
        value: ALERT_CRITICAL,
    },
    [ALERT_EXCESSIVE_OPTION]: {
        display: t`Excessive`,
        value: ALERT_EXCESSIVE,
    },
    [ALERT_DEGRADED_OPTION]: {
        display: t`Degraded`,
        value: ALERT_DEGRADED,
    },
};

export const SNMPV3_PROTOCOLS = ['None', 'HMAC-MD5', 'HMAC-SHA', 'HMAC-SHA256'];
export const SNMPV3_PRIVACY_PROTOCOLS = [
    'None',
    'AES-128',
    'AES-192',
    'AES-256',
    'AES-192-DES',
    'AES-256-DES',
    'CBC-DES',
];

export const SNMP_VERSION = {
    V2C: 'SNMP v2c',
    V3: 'SNMP v3',
};

export const SNMP_VERSION_DEFAULT = SNMP_VERSION.V2C;

export const SNMP_VERSION_OPTIONS = [
    {
        value: SNMP_VERSION.V2C,
        display: t`SNMP v2c`,
    },
    {
        value: SNMP_VERSION.V3,
        display: t`SNMP v3`,
    },
];

export const POLICY_NAMES = {
    serviceTest: 'serviceTest',
    site: 'site',
    businessService: 'businessService',
    interface: 'interface',
    device: 'device',
};

export const POLICY_TABS = Object.values(POLICY_NAMES);

export const POLICIES = {
    [POLICY_NAMES.serviceTest]: {
        name: POLICY_NAMES.serviceTest,
        index: 0,
        title: t`Service Test`,
        titlePlural: t`Service Tests`,
        toggleLabel: t`Send Service Test Notifications`,
        hasCritical: true,
    },
    [POLICY_NAMES.site]: {
        name: POLICY_NAMES.site,
        index: 1,
        title: t`Site`,
        titlePlural: t`Sites`,
        toggleLabel: t`Send Site Notifications`,
        hasCritical: true,
    },
    [POLICY_NAMES.businessService]: {
        name: POLICY_NAMES.businessService,
        index: 2,
        title: t`Business Service`,
        titlePlural: t`Business Services`,
        toggleLabel: t`Send Business Service Notifications`,
        hasCritical: true,
    },
    [POLICY_NAMES.interface]: {
        name: POLICY_NAMES.interface,
        index: 3,
        title: t`Interface`,
        titlePlural: t`High-Priority Interfaces`,
        toggleLabel: t`Send Interface Notifications`,
        hasCritical: true,
    },
    [POLICY_NAMES.device]: {
        name: POLICY_NAMES.device,
        index: 4,
        title: t`Network Device`,
        titlePlural: t`Devices`,
        toggleLabel: t`Send Network Device Notifications`,
        hasCritical: false,
    },
};

const NETWORK_DEVICE_TYPES = ['router', 'switch', 'wirelesscontroller', 'accesspoint', 'firewall', 'packet-processor'];

export const getNotifyTableColumns = (selectedType) => {
    return [
        {
            type: COLUMN_TYPES.TEXT,
            mapping: NOTIFY_EMAIL_FIELD,
            title: t`Email`,
            sortable: true,
            width: 350,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: NOTIFY_ALL_FIELD,
            title: t`Selection`,
            sortable: false,
            formatter: (value, row) => {
                if (row.all) {
                    return t`All ${selectedType.titlePlural}`;
                }

                return t`${row.ids.length} ${selectedType.title}`;
            },
        },
    ];
};

export const getSnmpTableColumns = (selectedType) => {
    return [
        {
            type: COLUMN_TYPES.TEXT,
            mapping: SNMP_SERVER_FIELD,
            title: t`SNMP Collector`,
            sortable: true,
            sortProperty: 'norm_server',
            width: 250,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: SNMP_VERSION_FIELD,
            title: t`Version`,
            sortable: false,
            width: 90,
            formatter(value) {
                return getSnmpVersionDisplay(value);
            },
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: SNMP_VERSION_FIELD,
            title: t`Community/Username`,
            sortable: false,
            width: 200,
            formatter(value, row) {
                const version = getSnmpVersionOrDefault(row.version);

                if (isSnmpV2c(version)) {
                    return '........';
                }
                if (isSnmpV3(version)) {
                    return row.userName || '';
                }
                return '';
            },
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: NOTIFY_ALL_FIELD,
            title: t`Selection`,
            sortable: false,
            formatter: (value, row) => {
                if (row.all) {
                    return t`All ${selectedType.titlePlural}`;
                }

                return t`${row.ids.length} ${selectedType.title}`;
            },
        },
    ];
};

export const getDialogTableColumns = (notifyType) => {
    let columns;
    switch (notifyType.name) {
        case POLICY_NAMES.site:
            columns = SITE_COLUMNS;
            break;
        case POLICY_NAMES.businessService:
            columns = BS_COLUMNS;
            break;
        case POLICY_NAMES.interface:
            columns = INTERFACE_COLUMNS;
            break;
        case POLICY_NAMES.device:
            columns = DEVICE_COLUMNS;
            break;
        default:
            columns = SERVICE_TEST_COLUMNS;
            break;
    }
    return columns;
};

const SITE_COLUMNS = [
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_NAME_FIELD,
        title: t`Name`,
        sortable: true,
        width: 300,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_DESCRIPTION_FIELD,
        title: t`Description`,
        sortable: true,
        width: 300,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_NOTES_FIELD,
        title: t`Notes`,
        sortable: true,
        minWidth: 200,
        flex: 1,
    },
];

const BS_COLUMNS = [
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_NAME_FIELD,
        title: t`Name`,
        sortable: true,
        width: 400,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_DESCRIPTION_FIELD,
        title: t`Description`,
        sortable: true,
        minWidth: 300,
        flex: 1,
    },
];

const SERVICE_TEST_COLUMNS = [
    {
        type: COLUMN_TYPES.ICON,
        mapping: NOTIFY_LIST_TABLE_TESTTYPEOBJECT_FIELD,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        width: 45,
        icons: [
            {
                // eslint-disable-next-line react/jsx-filename-extension
                getIcon: (row) => <MaterialUiIcon icon={row?.testTypeObject?.details?.icon} />,
                getTooltip: (row) => row?.testTypeObject?.details?.title,
            },
        ],
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_NAME_FIELD,
        title: t`Name`,
        sortable: true,
        width: 300,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_TAGS_FIELD,
        title: t`Tags`,
        sortable: false,
        flex: 1,
        minWidth: 500,
        formatter: standardizeTags,
    },
];

const INTERFACE_COLUMNS = [
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_NAME_FIELD,
        title: t`Name`,
        sortable: true,
        width: 300,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_DEVICE_NAME_FIELD,
        title: t`Device Name`,
        sortable: true,
        width: 180,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_DEVICE_IPADDRESS_FIELD,
        title: t`Device IP`,
        sortable: true,
        width: 120,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_DESCRIPTION_FIELD,
        title: t`Description`,
        minWidth: 200,
        flex: 1,
    },
];

const DEVICE_COLUMNS = [
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_NAME_FIELD,
        title: t`Name`,
        sortable: true,
        width: 300,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_IPADDRESS_FIELD,
        title: t`Device IP`,
        sortable: true,
        width: 120,
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: NOTIFY_LIST_TABLE_DESCRIPTION_FIELD,
        title: t`Description`,
        minWidth: 200,
        flex: 1,
    },
];

export const DATASOURCE_TYPE = {
    selectedDataSource: 'selectedDataSource',
    availableDataSource: 'availableDataSource',
    idsDataSource: 'idsDataSource',
};

export const getAddButtonText = (selectedType) => {
    return t`Add ${selectedType.titlePlural}`;
};
export const getEmptyGridText = (selectedType) => {
    return t`Click the Add ${selectedType.titlePlural} button above to add ${selectedType.title}s to this notification`;
};

export const getDialogTableDataSource = (notifyType) => {
    let dataSource;
    switch (notifyType.name) {
        case POLICY_NAMES.site:
            dataSource = {
                [DATASOURCE_TYPE.selectedDataSource]: siteSelectedDataSource,
                [DATASOURCE_TYPE.availableDataSource]: siteAvailableDataSource,
                [DATASOURCE_TYPE.idsDataSource]: siteIdsDataSource,
            };
            break;
        case POLICY_NAMES.businessService:
            dataSource = {
                [DATASOURCE_TYPE.selectedDataSource]: bsSelectedDataSource,
                [DATASOURCE_TYPE.availableDataSource]: bsAvailableDataSource,
                [DATASOURCE_TYPE.idsDataSource]: bsIdsDataSource,
            };
            break;
        case POLICY_NAMES.interface:
            dataSource = {
                [DATASOURCE_TYPE.selectedDataSource]: interfaceSelectedDataSource,
                [DATASOURCE_TYPE.availableDataSource]: interfaceAvailableDataSource,
                [DATASOURCE_TYPE.idsDataSource]: interfaceIdsDataSource,
            };
            break;
        case POLICY_NAMES.device:
            dataSource = {
                [DATASOURCE_TYPE.selectedDataSource]: deviceSelectedDataSource,
                [DATASOURCE_TYPE.availableDataSource]: deviceAvailableDataSource,
                [DATASOURCE_TYPE.idsDataSource]: deviceIdsDataSource,
            };
            break;
        default:
            dataSource = {
                [DATASOURCE_TYPE.selectedDataSource]: servicetestSelectedDataSource,
                [DATASOURCE_TYPE.availableDataSource]: servicetestAvailableDataSource,
                [DATASOURCE_TYPE.idsDataSource]: servicetestIdsDataSource,
            };
            break;
    }
    return dataSource;
};

const siteSelectedDataSource = {
    url: '/query/admin/sites/findSelected',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: ['name', 'description', 'country', 'province', 'city', 'notes'],
    },
    initialSort: [{ property: 'name', direction: SORT_ASC }],
};

const siteAvailableDataSource = {
    ...siteSelectedDataSource,
    url: '/query/admin/sites/findAvailable',
};

const siteIdsDataSource = {
    params: siteSelectedDataSource.params,
    url: '/query/admin/sites/selectionUUIDs',
};

const bsSelectedDataSource = {
    url: '/query/admin/business-services/findSelected',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: ['name', 'description'],
    },
    initialSort: [{ property: 'name', direction: SORT_ASC }],
};

const bsAvailableDataSource = {
    ...bsSelectedDataSource,
    url: '/query/admin/business-services/findAvailable',
};

const bsIdsDataSource = {
    params: bsSelectedDataSource.params,
    url: '/query/admin/business-services/selectionUUIDs',
};

const interfaceSelectedDataSource = {
    url: '/query/admin/interfaces/findSelected',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: ['name', 'description', 'deviceName', 'deviceIpAddress'],
    },
    initialSort: [{ property: 'name', direction: SORT_ASC }],
};

const interfaceAvailableDataSource = {
    ...interfaceSelectedDataSource,
    url: '/query/admin/interfaces/findSelected',
    params: {
        ...interfaceSelectedDataSource.params,
        query: '{priority: true}',
    },
};

const interfaceIdsDataSource = {
    params: {
        ...interfaceSelectedDataSource.params,
        query: '{priority: true}',
    },
    url: '/query/admin/interfaces/selectionUUIDs',
};

const deviceSelectedDataSource = {
    // url: '/ipm/v1/admin/monitoredDeviceConfig/findSelected',
    url: '/query/admin/monitoredDeviceConfig/findSelected',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: ['name', 'description', 'ipAddress'],
        deleted: { $ne: true },
        state: 'active',
    },
    initialSort: [{ property: NAME_FIELD, direction: SORT_ASC }],
};

const deviceAvailableDataSource = {
    ...deviceSelectedDataSource,
    url: '/ipm/v1/admin/monitoredDeviceConfig/findExcluded',
    body: {
        types: NETWORK_DEVICE_TYPES,
    },
};

const deviceIdsDataSource = {
    params: {
        ...deviceSelectedDataSource.params,
        query: JSON.stringify({
            deviceType: { $in: NETWORK_DEVICE_TYPES },
            deleted: { $ne: true },
            state: 'active',
        }),
    },
    url: '/query/admin/monitoredDeviceConfig/selectionUUIDs',
};

const servicetestSelectedDataSource = {
    url: '/ipm/v1/admin/tests/findSelected',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: ['name', 'tags'],
    },
    initialSort: [{ property: NAME_FIELD, direction: SORT_ASC }],
};

const servicetestAvailableDataSource = {
    ...servicetestSelectedDataSource,
    url: '/ipm/v1/admin/tests/findExcluded',
};

const servicetestIdsDataSource = {
    params: servicetestSelectedDataSource.params,
    url: '/ipm/v1/admin/tests/selectionUUIDs',
};

export const getSnmpVersionOrDefault = (version) => {
    return version || SNMP_VERSION_DEFAULT;
};

const getSnmpVersionDisplay = (version) => {
    return getSnmpVersionOrDefault(version);
};

export const isSnmpV2c = (version) => {
    return getSnmpVersionOrDefault(version) === SNMP_VERSION.V2C;
};

export const isSnmpV3 = (version) => {
    return getSnmpVersionOrDefault(version) === SNMP_VERSION.V3;
};

export const PASSWORD_REPLACEMENT = 'changeme';
export const TYPE_PASSWORD = 'password';
export const TYPE_TEXT = 'text';
